'use client';

import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useId,
} from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { createPortal } from 'react-dom';
import { Input } from 'src/components/PulseUI/Input';
import { CircularProgress } from 'src/components/PulseUI/CircularProgress';
import { ClickAwayListener } from 'src/components/PulseUI/ClickAwayListener';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';
import ControlLabel from './ControlLabel';

interface AutocompleteOption {
  code?: string;
  value?: string;
  label?: string;
  [key: string]: unknown;
}

interface ControlledAutocompleteProps {
  name: string;
  label?: string;
  options?: AutocompleteOption[];
  required?: boolean;
  rules?: Record<string, unknown>;
  placeholder?: string;
  loading?: boolean;
  helperText?: string;
  hasTooltip?: boolean;
  tooltipText?: string;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Opts into server-side search: typed text is reported here and `options`
   * are rendered as given rather than filtered locally.
   */
  onInputChange?: (value: string) => void;
  /**
   * The input only searches: it never shows the selected value, and empties
   * after a pick. The caller renders the selection itself.
   */
  searchOnly?: boolean;
  /** Called with the full option on select. */
  onOptionSelect?: (option: AutocompleteOption) => void;
  emptyText?: string;
  /**
   * A row pinned below the options — shown even when there are no results —
   * for an escape hatch that isn't itself a value.
   */
  footerAction?: { label: string; onSelect: () => void };
}

const getOptionLabel = (option: AutocompleteOption): string => {
  return option?.label || option?.code || option?.value || '';
};

const getOptionValue = (option: AutocompleteOption): string => {
  return option?.code || option?.value || '';
};

const ControlledAutocomplete: React.FC<ControlledAutocompleteProps> = ({
  name,
  label = '',
  options = [],
  required = false,
  rules,
  placeholder = '',
  loading = false,
  helperText = 'This field is required',
  hasTooltip,
  tooltipText,
  size = 'md',
  onInputChange,
  searchOnly = false,
  onOptionSelect,
  emptyText = 'No options found',
  footerAction,
}) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  // The option Arrow keys have moved to; Enter picks it.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listboxId = useId();

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const errorMessage = helperText;
  const isRequired = required || !!rules?.required;
  const watchValue = watch(name);

  const selectedOption = useMemo(() => {
    if (!watchValue) return null;
    return options.find(
      (option) => option?.code === watchValue || option?.value === watchValue
    );
  }, [watchValue, options]);

  const selectedText =
    !searchOnly && selectedOption ? getOptionLabel(selectedOption) : '';

  const filteredOptions = useMemo(() => {
    if (onInputChange || !inputValue) return options;
    const searchTerm = inputValue.toLowerCase();
    return options.filter((option) =>
      getOptionLabel(option).toLowerCase().includes(searchTerm)
    );
  }, [options, inputValue, onInputChange]);

  // Options only render once loaded, and the list can shrink under the
  // highlight when results arrive, so an out-of-range index highlights nothing.
  const activeIndex =
    isOpen && !loading && highlightedIndex < filteredOptions.length
      ? highlightedIndex
      : -1;
  const getOptionId = (index: number) => `${listboxId}-option-${index}`;

  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const dropdownHeight = 256;
      const shouldOpenUpward = spaceBelow < 100 && rect.top > 150;

      setPosition({
        top: shouldOpenUpward
          ? rect.top + scrollY - dropdownHeight - 4
          : rect.bottom + scrollY + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
    }
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (activeIndex < 0 || !dropdownRef.current) return;
    dropdownRef.current
      .querySelector(`[data-index="${activeIndex}"]`)
      // Not implemented in jsdom.
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  // Mirror the selection while not typing — including when it's cleared from
  // outside, e.g. by a footer action or form reset.
  useEffect(() => {
    if (!isFocused) {
      setInputValue(selectedText);
    }
  }, [selectedText, isFocused]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsFocused(false);
    setHighlightedIndex(-1);
    setInputValue(selectedText);
  }, [selectedText]);

  const sizeClasses = {
    sm: 'min-h-8 text-xs',
    md: 'min-h-10 text-sm',
    lg: 'min-h-12 text-sm',
  };

  const hasError = !!errors[name];

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? errorMessage : false,
        ...rules,
      }}
      render={({ field }) => {
        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          setInputValue(e.target.value);
          setHighlightedIndex(-1);
          onInputChange?.(e.target.value);
          if (!isOpen) {
            setIsOpen(true);
          }
        };

        const handleInputFocus = () => {
          setIsFocused(true);
          setIsOpen(true);
          setHighlightedIndex(-1);
          setInputValue('');
          onInputChange?.('');
        };

        const handleOptionSelect = (option: AutocompleteOption) => {
          const value = getOptionValue(option);
          field.onChange(value || null);
          onOptionSelect?.(option);
          if (searchOnly) {
            setInputValue('');
            onInputChange?.('');
          } else {
            setInputValue(getOptionLabel(option));
          }
          setIsOpen(false);
          setIsFocused(false);
          setHighlightedIndex(-1);
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          switch (e.key) {
            case 'ArrowDown':
              e.preventDefault();
              setIsOpen(true);
              setHighlightedIndex((prev) =>
                Math.min(prev + 1, filteredOptions.length - 1)
              );
              break;
            case 'ArrowUp':
              e.preventDefault();
              setHighlightedIndex((prev) => Math.max(prev - 1, 0));
              break;
            case 'Enter':
              // Never submits the surrounding form: Enter here is a search
              // action, not a save.
              e.preventDefault();
              if (activeIndex >= 0) {
                handleOptionSelect(filteredOptions[activeIndex]);
              }
              break;
            case 'Escape':
              if (isOpen) handleClose();
              break;
          }
        };

        const handleToggle = (e: React.MouseEvent) => {
          // A click on the input only ever opens: focusing it already opened
          // the dropdown, so toggling would close it again. It can still be
          // closed while focused (after picking), so reopen in that case.
          if (e.target === inputRef.current) {
            if (!isOpen) handleInputFocus();
            return;
          }
          if (!isOpen) {
            inputRef.current?.focus();
          } else {
            handleClose();
          }
        };

        return (
          <ControlLabel
            label={label}
            required={isRequired}
            hasTooltip={hasTooltip}
            tooltipText={tooltipText}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <div ref={containerRef} className="relative">
                <div
                  className={twMerge(
                    'flex items-center w-full rounded-full border bg-white transition-all duration-200',
                    hasError
                      ? 'border-red-500 hover:border-red-500'
                      : isFocused
                      ? 'border-neutral-500 ring-1 ring-neutral-500/20'
                      : 'border-neutral-200 hover:border-neutral-500',
                    sizeClasses[size]
                  )}
                  onClick={handleToggle}
                  data-testid={`${name}-autocomplete`}
                >
                  <Input
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? listboxId : undefined}
                    aria-activedescendant={
                      activeIndex >= 0 ? getOptionId(activeIndex) : undefined
                    }
                    placeholder={selectedText || placeholder}
                    className="rouned-full flex-grow bg-transparent border-none focus:ring-0 px-3"
                    aria-label={label || placeholder}
                    data-testid={`${name}-autocomplete-input`}
                  />
                  <span className="flex items-center pr-3 text-neutral-500">
                    {loading ? (
                      <CircularProgress size="sm" />
                    ) : (
                      <Icon
                        icon={faCaretDown}
                        className={twMerge(
                          'w-3.5 h-3.5 transition-transform duration-200',
                          isOpen ? '-rotate-90' : 'rotate-0'
                        )}
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </div>

                {isOpen &&
                  typeof document !== 'undefined' &&
                  createPortal(
                    <div
                      ref={dropdownRef}
                      id={listboxId}
                      role="listbox"
                      // Portaled outside the ClickAwayListener; this marks it as
                      // inside so choosing an option isn't a click-away.
                      data-portal-content
                      className="fixed z-[9999] bg-white border border-neutral-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                      data-testid={`${name}-autocomplete-dropdown`}
                      style={{
                        top: position.top,
                        left: position.left,
                        width: position.width,
                      }}
                    >
                      {loading ? (
                        <div className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-600">
                          <CircularProgress size="sm" />
                          Loading...
                        </div>
                      ) : filteredOptions.length === 0 ? (
                        <div
                          className="px-3 py-2 text-sm text-neutral-600"
                          data-testid={`${name}-autocomplete-empty`}
                        >
                          {emptyText}
                        </div>
                      ) : (
                        filteredOptions.map((option, index) => {
                          const optionValue = getOptionValue(option);
                          const isSelected = optionValue === watchValue;
                          return (
                            <div
                              key={optionValue || index}
                              id={getOptionId(index)}
                              data-index={index}
                              className={twMerge(
                                'px-3 py-2 text-sm cursor-pointer hover:bg-neutral-100 transition-colors',
                                isSelected && 'bg-neutral-50',
                                index === activeIndex && 'bg-neutral-100'
                              )}
                              onClick={() => handleOptionSelect(option)}
                              role="option"
                              aria-selected={isSelected}
                              data-testid={`${name}-option-${optionValue}`}
                            >
                              {getOptionLabel(option)}
                            </div>
                          );
                        })
                      )}
                      {footerAction && (
                        <div className="sticky bottom-0 bg-white border-t border-neutral-200">
                          <PulseButton
                            variant="plain"
                            color="neutral"
                            size="sm"
                            className="w-full justify-start rounded-none"
                            onClick={() => {
                              handleClose();
                              footerAction.onSelect();
                            }}
                            aria-label={footerAction.label}
                            data-testid={`${name}-autocomplete-footer-action`}
                          >
                            {footerAction.label}
                          </PulseButton>
                        </div>
                      )}
                    </div>,
                    document.body
                  )}
              </div>
            </ClickAwayListener>
            {hasError && (
              <p className="text-red-500 text-xs mt-1">
                {errors[name]?.message as string}
              </p>
            )}
          </ControlLabel>
        );
      }}
    />
  );
};

export default ControlledAutocomplete;
